import React from 'react';
import { Link } from 'react-router-dom';
import { LuArrowRight, LuUserPlus, LuPlug, LuMessageCircle } from 'react-icons/lu';
import useInView from '../hooks/useInView';

const BLOCKS = [
  {
    eyebrow: 'Sign up',
    title: 'Create a free account',
    body: 'Spin up an account and link your first SIM in under five minutes.',
    href: '/signup',
    Icon: LuUserPlus
  },
  {
    eyebrow: 'GoHighLevel',
    title: 'Plug into GHL',
    body: 'Swap Twilio for TextLink inside GoHighLevel — same workflows, fraction of the cost.',
    href: '/ghl',
    Icon: LuPlug
  },
  {
    eyebrow: 'Talk to us',
    title: 'Talk to a human',
    body: 'Got an unusual use case? Ask — we respond the same day.',
    href: '/Contact',
    Icon: LuMessageCircle
  }
];

export default function HomeCtaHub() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`hch${inView ? ' in-view' : ''}`}>
      <div className="hch-container">
        <h2 className="hch-title">
          Ready to start?<br />
          <span className="hch-title-muted">Pick your entry point — account, GHL, or a chat.</span>
        </h2>

        <div className="hch-grid">
          {BLOCKS.map(({ eyebrow, title, body, href, Icon }, i) => (
            <Link key={i} to={href} className="hch-card">
              <div className="hch-card-head">
                <span className="hch-icon">
                  <Icon size={18} strokeWidth={1.75} />
                </span>
                <span className="hch-eyebrow">{eyebrow}</span>
              </div>

              <div className="hch-card-foot">
                <div className="hch-card-text">
                  <h3 className="hch-card-title">{title}</h3>
                  <p className="hch-card-body">{body}</p>
                </div>
                <span className="hch-arrow" aria-hidden="true">
                  <LuArrowRight size={16} strokeWidth={2.25} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <style jsx>{`
        .hch {
          background: #ffffff;
          color: #0a0a0a;
          padding: clamp(6rem, 14vh, 11rem) clamp(1rem, 3vw, 2rem);
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          -webkit-font-smoothing: antialiased;
          width: 100%;
          max-width: 100vw;
          box-sizing: border-box;
        }
        .hch-container {
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
          box-sizing: border-box;
        }

        .hch-title {
          font-size: clamp(2.25rem, 5.5vw, 3.75rem);
          font-weight: 600;
          letter-spacing: -0.045em;
          line-height: 1.08;
          margin: 0 0 clamp(2.5rem, 5vh, 3.75rem);
          max-width: 22ch;
          color: #0a0a0a;
        }
        .hch-title-muted {
          color: rgba(0,0,0,0.5);
          font-weight: 500;
        }

        .hch-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: clamp(0.875rem, 1.25vw, 1.125rem);
          width: 100%;
        }
        @media (max-width: 900px) {
          .hch-grid { grid-template-columns: minmax(0, 1fr); }
        }

        .hch-card {
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 16px;
          padding: clamp(1.5rem, 2vw, 1.875rem);
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          gap: clamp(3rem, 7vw, 4.5rem);
          min-height: 220px;
          transition:
            border-color 220ms ease,
            transform 220ms ease,
            box-shadow 220ms ease,
            opacity 650ms cubic-bezier(0.16, 1, 0.3, 1),
            translate 650ms cubic-bezier(0.16, 1, 0.3, 1);
          opacity: 0;
          translate: 0 18px;
        }
        .hch.in-view .hch-card { opacity: 1; translate: 0 0; }
        .hch.in-view .hch-card:nth-child(1) { transition-delay: 80ms, 80ms, 80ms, 80ms, 80ms; }
        .hch.in-view .hch-card:nth-child(2) { transition-delay: 80ms, 80ms, 80ms, 180ms, 180ms; }
        .hch.in-view .hch-card:nth-child(3) { transition-delay: 80ms, 80ms, 80ms, 280ms, 280ms; }

        .hch-card:hover {
          border-color: rgba(124, 58, 237, 0.35);
          transform: translateY(-2px);
          box-shadow: 0 14px 32px -18px rgba(124, 58, 237, 0.28);
        }

        .hch-title {
          opacity: 0;
          translate: 0 16px;
          transition:
            opacity 650ms cubic-bezier(0.16, 1, 0.3, 1),
            translate 650ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hch.in-view .hch-title { opacity: 1; translate: 0 0; }

        .hch-card-head {
          display: flex;
          align-items: center;
          gap: 0.625rem;
        }
        .hch-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: #f5f3ff;
          color: #6d28d9;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 220ms ease, color 220ms ease;
        }
        .hch-card:hover .hch-icon {
          background: #ede9fe;
          color: #5b21b6;
        }
        .hch-eyebrow {
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(0,0,0,0.45);
        }

        .hch-card-foot {
          display: flex;
          align-items: flex-end;
          gap: 1rem;
          margin-top: auto;
        }
        .hch-card-text { flex: 1; min-width: 0; }
        .hch-card-title {
          font-size: 1.125rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          margin: 0 0 0.4rem;
          color: #0a0a0a;
          line-height: 1.25;
        }
        .hch-card-body {
          font-size: 0.9375rem;
          color: rgba(0,0,0,0.55);
          line-height: 1.5;
          margin: 0;
        }
        .hch-arrow {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: #0a0a0a;
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 220ms cubic-bezier(0.16, 1, 0.3, 1), background 220ms ease;
        }
        .hch-card:hover .hch-arrow {
          transform: translate(3px, -3px) rotate(-8deg);
          background: #7c3aed;
        }
      `}</style>
    </section>
  );
}
